import {
  SOCCER_BLUE_FIELD,
  SOCCER_ORANGE_FIELD,
  SOCCER_TEAM_BLUE,
  SOCCER_TEAM_ORANGE,
  soccerGoalVisualRect,
  soccerPosts,
  soccerVariantConfig,
  type SoccerPickupState,
  type SoccerSnapshot,
  type SoccerTeam,
} from "../../../lib/multiplayer/soccer-sim";
import { isSoccerMobileView, isSoccerTouchBoostUi, soccerScale, worldToScreen, type SoccerView } from "./view";

function fieldCfg(view: SoccerView) {
  return soccerVariantConfig(view.variant);
}

export function renderSoccerField(ctx: CanvasRenderingContext2D, view: SoccerView): void {
  const cfg = fieldCfg(view);
  const worldW = view.worldW || cfg.w;
  const worldH = view.worldH || cfg.h;
  ctx.save();
  ctx.beginPath();
  ctx.rect(view.playX, view.playY, view.playW, view.playH);
  ctx.clip();

  const mid = worldToScreen(worldW / 2, worldH / 2, view);
  const vertical = view.mode === "mobile" || view.mode === "local-mobile";
  const perspective = view.mode === "pc" || view.mode === "mobile";
  const localField = view.localTeam === "blue" ? SOCCER_BLUE_FIELD : SOCCER_ORANGE_FIELD;
  const oppField = view.localTeam === "blue" ? SOCCER_ORANGE_FIELD : SOCCER_BLUE_FIELD;

  if (vertical) {
    const split = mid.y;
    if (perspective) {
      ctx.fillStyle = oppField;
      ctx.fillRect(view.playX, view.playY, view.playW, split - view.playY);
      ctx.fillStyle = localField;
      ctx.fillRect(view.playX, split, view.playW, view.playY + view.playH - split);
    } else {
      ctx.fillStyle = SOCCER_ORANGE_FIELD;
      ctx.fillRect(view.playX, view.playY, view.playW, split - view.playY);
      ctx.fillStyle = SOCCER_BLUE_FIELD;
      ctx.fillRect(view.playX, split, view.playW, view.playY + view.playH - split);
    }
  } else {
    const split = mid.x;
    if (perspective) {
      ctx.fillStyle = oppField;
      ctx.fillRect(view.playX, view.playY, split - view.playX, view.playH);
      ctx.fillStyle = localField;
      ctx.fillRect(split, view.playY, view.playX + view.playW - split, view.playH);
    } else {
      ctx.fillStyle = SOCCER_BLUE_FIELD;
      ctx.fillRect(view.playX, view.playY, split - view.playX, view.playH);
      ctx.fillStyle = SOCCER_ORANGE_FIELD;
      ctx.fillRect(split, view.playY, view.playX + view.playW - split, view.playH);
    }
  }

  ctx.strokeStyle = "rgba(255,255,255,0.72)";
  ctx.lineWidth = Math.max(2, 2.4 * soccerScale(view) * 20);
  ctx.strokeRect(view.playX + 2, view.playY + 2, view.playW - 4, view.playH - 4);

  ctx.beginPath();
  if (vertical) {
    ctx.moveTo(view.playX, mid.y);
    ctx.lineTo(view.playX + view.playW, mid.y);
  } else {
    ctx.moveTo(mid.x, view.playY);
    ctx.lineTo(mid.x, view.playY + view.playH);
  }
  ctx.stroke();

  const center = worldToScreen(worldW / 2, worldH / 2, view);
  const cr =
    Math.abs(worldToScreen(worldW / 2 + cfg.w * 0.0875, worldH / 2, view).x - center.x) ||
    view.playW * 0.12;
  ctx.beginPath();
  ctx.arc(center.x, center.y, cr, 0, Math.PI * 2);
  ctx.stroke();
  ctx.fillStyle = "#fff";
  ctx.beginPath();
  ctx.arc(center.x, center.y, Math.max(3, soccerScale(view) * 6), 0, Math.PI * 2);
  ctx.fill();

  drawGoalBox(ctx, view, cfg, "blue");
  drawGoalBox(ctx, view, cfg, "orange");
  ctx.restore();
}

function drawGoalBox(
  ctx: CanvasRenderingContext2D,
  view: SoccerView,
  cfg: ReturnType<typeof soccerVariantConfig>,
  team: SoccerTeam,
): void {
  const g = soccerGoalVisualRect(team, cfg);
  const a = worldToScreen(g.x, g.y, view);
  const b = worldToScreen(g.x + g.w, g.y + g.h, view);
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  const w = Math.abs(b.x - a.x);
  const h = Math.abs(b.y - a.y);
  ctx.fillStyle = team === "blue" ? "rgba(90,165,255,0.42)" : "rgba(255,165,80,0.42)";
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = "rgba(255,255,255,0.95)";
  ctx.lineWidth = Math.max(3, soccerScale(view) * 10);
  ctx.strokeRect(x, y, w, h);
  ctx.strokeStyle = "rgba(255,255,255,0.55)";
  ctx.lineWidth = Math.max(1.5, soccerScale(view) * 4);
  const inner = Math.max(4, soccerScale(view) * 6);
  ctx.strokeRect(x + inner, y + inner, Math.max(0, w - inner * 2), Math.max(0, h - inner * 2));
  for (const post of soccerPosts(cfg)) {
    if ((team === "blue" && post.x > 1) || (team === "orange" && post.x < cfg.w - 1)) continue;
    const p = worldToScreen(post.x, post.y, view);
    ctx.fillStyle = "#f4f1ea";
    ctx.beginPath();
    ctx.arc(p.x, p.y, Math.max(4, post.r * soccerScale(view)), 0, Math.PI * 2);
    ctx.fill();
    ctx.strokeStyle = "rgba(0,0,0,0.35)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
  }
}

export function renderSoccerBall(
  ctx: CanvasRenderingContext2D,
  view: SoccerView,
  x: number,
  y: number,
  replay = false,
  angle = 0,
): void {
  const s = worldToScreen(x, y, view);
  const r = fieldCfg(view).ballR * soccerScale(view);
  ctx.save();
  ctx.translate(s.x, s.y);
  ctx.rotate(angle);
  const grd = ctx.createRadialGradient(-r * 0.3, -r * 0.35, r * 0.1, 0, 0, r);
  grd.addColorStop(0, "#ffffff");
  grd.addColorStop(1, replay ? "#e8d8d8" : "#d8d8d8");
  ctx.fillStyle = grd;
  ctx.beginPath();
  ctx.arc(0, 0, r, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "rgba(20,20,20,0.85)";
  ctx.lineWidth = Math.max(1.5, r * 0.08);
  ctx.stroke();
  ctx.fillStyle = "#111";
  for (let i = 0; i < 5; i++) {
    const a = (i / 5) * Math.PI * 2 - Math.PI / 2;
    ctx.beginPath();
    ctx.arc(Math.cos(a) * r * 0.42, Math.sin(a) * r * 0.42, r * 0.16, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function teamHudAnchors(view: SoccerView, ls: number): {
  blue: { x: number; y: number };
  orange: { x: number; y: number };
  clock: { x: number; y: number };
} {
  const cx = view.playX + view.playW / 2;
  const vertical = view.mode === "mobile" || view.mode === "local-mobile";
  const perspective = view.mode === "pc" || view.mode === "mobile";
  const topHudY = Math.max(26, view.playY - 14 * ls);

  if (vertical && perspective) {
    const blueBottom = view.localTeam === "blue";
    return {
      blue: {
        x: cx,
        y: blueBottom ? view.playY + view.playH - 16 * ls : view.playY + 16 * ls,
      },
      orange: {
        x: cx,
        y: blueBottom ? view.playY + 16 * ls : view.playY + view.playH - 16 * ls,
      },
      clock: { x: cx, y: topHudY },
    };
  }
  if (vertical) {
    return {
      blue: { x: cx, y: view.playY + view.playH - 16 * ls },
      orange: { x: cx, y: view.playY + 16 * ls },
      clock: { x: cx, y: topHudY },
    };
  }
  if (perspective) {
    const blueRight = view.localTeam === "blue";
    return {
      blue: {
        x: view.playX + view.playW * (blueRight ? 0.74 : 0.26),
        y: topHudY + 18 * ls,
      },
      orange: {
        x: view.playX + view.playW * (blueRight ? 0.26 : 0.74),
        y: topHudY + 18 * ls,
      },
      clock: { x: cx, y: topHudY },
    };
  }
  return {
    blue: { x: view.playX + view.playW * 0.26, y: topHudY + 18 * ls },
    orange: { x: view.playX + view.playW * 0.74, y: topHudY + 18 * ls },
    clock: { x: cx, y: topHudY },
  };
}

export function renderSoccerHud(
  ctx: CanvasRenderingContext2D,
  canvasW: number,
  canvasH: number,
  snap: SoccerSnapshot,
  view: SoccerView,
): void {
  const ls = Math.max(0.75, Math.min(canvasW, canvasH) / 720);
  ctx.save();
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.font = `700 ${Math.round(18 * ls)}px "Press Start 2P", monospace`;

  if (snap.fiveMinutesAtTop || snap.phase === "playing" || snap.phase === "overtime" || snap.phase === "replay") {
    const anchors = teamHudAnchors(view, ls);
    const clock =
      snap.phase === "overtime" || (snap.phase === "replay" && snap.overtimeTime > 0)
        ? `+${formatClock(snap.overtimeTime)}`
        : formatClock(snap.timeLeft);
    ctx.fillStyle = "#fff";
    ctx.strokeStyle = "rgba(0,0,0,0.7)";
    ctx.lineWidth = 4;
    ctx.strokeText(clock, anchors.clock.x, anchors.clock.y);
    ctx.fillText(clock, anchors.clock.x, anchors.clock.y);
    ctx.font = `700 ${Math.round(16 * ls)}px "Press Start 2P", monospace`;
    ctx.fillStyle = "#fff";
    ctx.strokeText(String(snap.scoreBlue), anchors.blue.x, anchors.blue.y - 14 * ls);
    ctx.fillText(String(snap.scoreBlue), anchors.blue.x, anchors.blue.y - 14 * ls);
    ctx.strokeText(String(snap.scoreOrange), anchors.orange.x, anchors.orange.y - 14 * ls);
    ctx.fillText(String(snap.scoreOrange), anchors.orange.x, anchors.orange.y - 14 * ls);
    ctx.font = `700 ${Math.round(10 * ls)}px "Press Start 2P", monospace`;
    ctx.fillStyle = SOCCER_TEAM_BLUE;
    ctx.fillText("BLUE", anchors.blue.x, anchors.blue.y);
    ctx.fillStyle = SOCCER_TEAM_ORANGE;
    ctx.fillText("ORANGE", anchors.orange.x, anchors.orange.y);
  }

  if (snap.phase === "countdown" || snap.phase === "overtime_countdown") {
    const n = Math.ceil(snap.countdown);
    ctx.font = `700 ${Math.round(72 * ls)}px "Press Start 2P", monospace`;
    ctx.fillStyle = "#fff";
    ctx.strokeStyle = "rgba(0,0,0,0.75)";
    ctx.lineWidth = 8;
    const text = snap.goVisible ? "GO" : n >= 1 ? String(Math.min(3, n)) : "GO";
    ctx.strokeText(text, canvasW / 2, canvasH / 2);
    ctx.fillText(text, canvasW / 2, canvasH / 2);
    if (snap.goVisible && snap.showFiveMinutes) {
      ctx.font = `700 ${Math.round(12 * ls)}px "Press Start 2P", monospace`;
      const matchSec = fieldCfg(view).matchSec;
      const minLabel =
        matchSec >= 120 ? `${Math.round(matchSec / 60)} minutes` : `${matchSec} seconds`;
      ctx.fillText(minLabel, canvasW / 2, canvasH / 2 + 52 * ls);
    }
  }

  if (snap.timeBanner) {
    ctx.font = `700 ${Math.round(22 * ls)}px "Press Start 2P", monospace`;
    ctx.fillStyle = "rgba(255,255,255,0.92)";
    ctx.strokeStyle = "rgba(0,0,0,0.65)";
    ctx.lineWidth = 5;
    ctx.strokeText(snap.timeBanner.text, canvasW / 2, canvasH * 0.28);
    ctx.fillText(snap.timeBanner.text, canvasW / 2, canvasH * 0.28);
  }

  const liveMsg = snap.messages.length ? snap.messages[snap.messages.length - 1]! : null;
  if (liveMsg) {
    ctx.font = `700 ${Math.round(28 * ls)}px "Press Start 2P", monospace`;
    ctx.fillStyle = liveMsg.kind === "goal" ? "#ffe566" : "#ffffff";
    ctx.shadowColor = liveMsg.kind === "goal" ? "#ffaa00" : "#66ffff";
    ctx.shadowBlur = 18;
    ctx.strokeStyle = "rgba(0,0,0,0.7)";
    ctx.lineWidth = 6;
    ctx.strokeText(liveMsg.text, canvasW / 2, canvasH * 0.38);
    ctx.fillText(liveMsg.text, canvasW / 2, canvasH * 0.38);
  }
  ctx.shadowBlur = 0;

  if (snap.phase === "replay") {
    ctx.strokeStyle = "rgba(220,30,30,0.85)";
    ctx.lineWidth = 10;
    ctx.strokeRect(6, 6, canvasW - 12, canvasH - 12);
    ctx.fillStyle = "#ff2a2a";
    ctx.beginPath();
    ctx.arc(36, 34, 8, 0, Math.PI * 2);
    ctx.fill();
    ctx.font = `700 ${Math.round(12 * ls)}px "Press Start 2P", monospace`;
    ctx.fillStyle = "#fff";
    ctx.textAlign = "left";
    ctx.fillText("REC", 52, 36);
  }
  ctx.restore();
}

export function renderSoccerReplaySkip(
  ctx: CanvasRenderingContext2D,
  canvasW: number,
  canvasH: number,
  awaitingNames: string[],
): void {
  const ls = Math.max(0.75, Math.min(canvasW, canvasH) / 720);
  const pad = Math.max(14, canvasW * 0.022);
  const skipBtnReserve = Math.max(48, canvasH * 0.068);
  const baseY = canvasH - pad - skipBtnReserve;
  ctx.save();
  ctx.textAlign = "right";
  ctx.textBaseline = "bottom";
  if (!isSoccerMobileView()) {
    ctx.font = `700 ${Math.round(9 * ls)}px "Press Start 2P", monospace`;
    ctx.fillStyle = "rgba(255,255,255,0.82)";
    ctx.strokeStyle = "rgba(0,0,0,0.65)";
    ctx.lineWidth = 3;
    const hint = "Click to Skip Replay";
    ctx.strokeText(hint, canvasW - pad, baseY - (awaitingNames.length ? 18 * ls : 0));
    ctx.fillText(hint, canvasW - pad, baseY - (awaitingNames.length ? 18 * ls : 0));
  }
  if (awaitingNames.length) {
    ctx.font = `700 ${Math.round(8 * ls)}px "Press Start 2P", monospace`;
    ctx.fillStyle = "rgba(200,220,255,0.9)";
    ctx.strokeStyle = "rgba(0,0,0,0.65)";
    ctx.lineWidth = 3;
    const list = awaitingNames.join(", ");
    ctx.strokeText(list, canvasW - pad, baseY);
    ctx.fillText(list, canvasW - pad, baseY);
  }
  ctx.restore();
}

function formatClock(sec: number): string {
  const s = Math.max(0, Math.ceil(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, "0")}`;
}

export function soccerCursorRadius(view: SoccerView): number {
  return fieldCfg(view).cursorR * soccerScale(view);
}

export function renderSoccerPickups(
  ctx: CanvasRenderingContext2D,
  view: SoccerView,
  pickups: SoccerPickupState[],
  simT: number,
): void {
  if (!pickups.length) return;
  const sc = soccerScale(view);
  ctx.save();
  ctx.beginPath();
  ctx.rect(view.playX, view.playY, view.playW, view.playH);
  ctx.clip();
  for (const pick of pickups) {
    const dim = pick.respawnAt != null;
    const s = worldToScreen(pick.x, pick.y, view);
    const baseR = pick.kind === "max" ? 22 * sc : 11 * sc;
    const r = Math.max(pick.kind === "max" ? 10 : 5, baseR);
    ctx.globalAlpha = dim ? 0.22 : 1;
    if (pick.kind === "max") {
      const grd = ctx.createRadialGradient(s.x - r * 0.25, s.y - r * 0.3, r * 0.05, s.x, s.y, r);
      grd.addColorStop(0, "#fff6b0");
      grd.addColorStop(0.45, "#ffd24a");
      grd.addColorStop(1, "#b8860b");
      ctx.fillStyle = grd;
      ctx.beginPath();
      ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "rgba(255,255,255,0.75)";
      ctx.lineWidth = Math.max(2, r * 0.12);
      ctx.stroke();
    } else {
      const grd = ctx.createRadialGradient(s.x - r * 0.2, s.y - r * 0.25, r * 0.05, s.x, s.y, r);
      grd.addColorStop(0, "#b8fff8");
      grd.addColorStop(0.5, "#3dd9c4");
      grd.addColorStop(1, "#0a7a72");
      ctx.fillStyle = grd;
      ctx.beginPath();
      ctx.arc(s.x, s.y, r, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = "rgba(200,255,250,0.65)";
      ctx.lineWidth = Math.max(1.5, r * 0.14);
      ctx.stroke();
    }
    if (dim && pick.respawnAt != null && simT > 0) {
      const left = Math.max(0, pick.respawnAt - simT);
      ctx.globalAlpha = 0.35;
      ctx.strokeStyle = "rgba(255,255,255,0.4)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(s.x, s.y, r * 0.55, 0, Math.PI * 2);
      ctx.stroke();
      void left;
    }
  }
  ctx.restore();
}

export function renderSoccerBoostRing(
  ctx: CanvasRenderingContext2D,
  screenX: number,
  screenY: number,
  charge: number,
  active: boolean,
  view: SoccerView,
): void {
  const sc = soccerScale(view);
  const outer = Math.max(14, 26 * sc);
  const inner = outer * 0.72;
  const c = Math.max(0, Math.min(1, charge));
  ctx.save();
  ctx.beginPath();
  ctx.arc(screenX, screenY, outer, 0, Math.PI * 2);
  ctx.strokeStyle = active ? "rgba(255,220,80,0.95)" : "rgba(120,200,255,0.55)";
  ctx.lineWidth = Math.max(2, 3.5 * sc);
  ctx.stroke();
  if (c > 0.02) {
    ctx.beginPath();
    ctx.arc(screenX, screenY, inner, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * c);
    ctx.strokeStyle = active ? "#ffe566" : "#66ccff";
    ctx.lineWidth = Math.max(2.5, 4 * sc);
    ctx.lineCap = "round";
    ctx.stroke();
  }
  ctx.restore();
}

export function renderSoccerBoostHint(
  ctx: CanvasRenderingContext2D,
  canvasW: number,
  canvasH: number,
  view: SoccerView,
): void {
  if (view.variant !== "mega" || isSoccerTouchBoostUi()) return;
  const ls = Math.max(0.75, Math.min(canvasW, canvasH) / 720);
  const pad = Math.max(12, canvasW * 0.02);
  const y = canvasH - pad - 8;
  const iconX = pad + 18 * ls;
  const iconY = y - 14 * ls;
  ctx.save();
  ctx.fillStyle = "rgba(255,255,255,0.88)";
  ctx.strokeStyle = "rgba(0,0,0,0.55)";
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(iconX, iconY - 10 * ls);
  ctx.lineTo(iconX + 8 * ls, iconY + 6 * ls);
  ctx.lineTo(iconX - 8 * ls, iconY + 6 * ls);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = "rgba(255,220,80,0.95)";
  ctx.fillRect(iconX - 5 * ls, iconY - 2 * ls, 10 * ls, 5 * ls);
  ctx.font = `700 ${Math.round(9 * ls)}px "Press Start 2P", monospace`;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillStyle = "rgba(255,255,255,0.9)";
  ctx.strokeStyle = "rgba(0,0,0,0.65)";
  ctx.lineWidth = 3;
  const text = "BOOST: LEFT CLICK";
  ctx.strokeText(text, iconX + 22 * ls, iconY);
  ctx.fillText(text, iconX + 22 * ls, iconY);
  ctx.restore();
}
